import { Link } from 'react-router';
import { ROLES, type Conversation, type ListenMode } from '@jabberr/core';
import { longDate, shortDate } from '../lib/format.js';
import { liveVersion, roleLabel, thinkingLabel } from '../lib/plain.js';
import { activeMemory, contextBullets, docSections, useKnows } from '../state/knows.js';
import { TalkModeControl } from './TalkModeControl.js';

/**
 * "What Jabberr is going on": the conversation's pinned picture in plain words (what is live, what is being built,
 * decisions, memory), then this conversation's own settings. A client-audience conversation shows only what a client
 * may see, in client words; the server already withholds the rest from a guest.
 */
export function GoingOnPanel({
  conversation: c,
  projectId,
  guest,
  listenBusy,
  onListenMode,
}: {
  conversation: Conversation;
  projectId: string;
  guest: boolean;
  listenBusy: boolean;
  onListenMode: (mode: ListenMode) => void;
}) {
  const client = guest || ROLES[c.role].audience === 'client';
  const knows = useKnows(projectId, { team: !client, outputs: false });
  const production = knows.state?.production ?? null;
  const dev = knows.state?.dev ?? null;
  const pinnedLive = c.stateVersions.production;
  const version = liveVersion(production?.content);
  const liveText = !production
    ? 'Nothing sent yet'
    : pinnedLive !== undefined && pinnedLive < production.version
      ? `An earlier version; a newer one arrived ${shortDate(production.createdAt)}`
      : `${version ? `Version ${version}, ` : ''}${longDate(production.createdAt)}`;
  const decisions = [...contextBullets(knows.context?.content, 'Decisions'), ...activeMemory(knows.memory?.project).filter((i) => i.kind === 'decision').map((i) => i.text)];
  const remembers = [...activeMemory(knows.memory?.project).filter((i) => i.kind !== 'decision'), ...activeMemory(knows.memory?.user)];
  const devLine = dev ? firstLine(dev.content) : null;
  // The conversation keeps what it started with (the pinned prefix); say so when the project has moved on since.
  const pinned = c.stateVersions;
  const movedOn =
    (production !== null && pinned.production !== undefined && pinned.production < production.version) ||
    (dev !== null && (pinned.dev === undefined || pinned.dev < dev.version)) ||
    (knows.context !== null && knows.context.version > c.contextVersion);

  return (
    <aside className="going-on" aria-label="What Jabberr is going on">
      <h2>What Jabberr is going on</h2>
      {knows.loading ? null : client ? (
        <>
          <Fact title="Your product as it is today" text={production ? `Updated ${longDate(production.createdAt)}` : 'Nothing shared yet'} />
          <p className="going-on-note">Jabberr only sees what is live and what you have told it. Nothing the team is still working on.</p>
        </>
      ) : (
        <>
          <Fact title="What's live now" text={liveText} />
          <Fact title="Being built" text={devLine ?? 'Nothing sent yet'} />
          <Fact title="Decisions" text={decisions.length === 0 ? 'None saved yet' : more(`“${decisions[0]}”`, decisions.length - 1)} />
          <Fact title="Remembers" text={remembers.length === 0 ? 'Nothing yet' : more(remembers[0]!.text, remembers.length - 1)} />
          <p className="going-on-note">
            {movedOn
              ? 'The project has changed since this conversation started. Jabberr here still works from what it started with (memory too); a new conversation picks up the latest.'
              : 'This is the project as it is now. Notes Jabberr picks up later reach new conversations.'}
          </p>
          <Link to={`/p/${projectId}/knows`} className="going-on-link">
            See everything Jabberr knows
          </Link>
        </>
      )}
      <div className="going-on-settings">
        <h3>In this conversation</h3>
        <dl>
          <dt>Jabberr as</dt>
          <dd>{roleLabel(c.role)}</dd>
          <dt>Thinking</dt>
          <dd>{thinkingLabel(c.reasoningEffort)}</dd>
          <dt>Web search</dt>
          <dd>Web search on</dd>
        </dl>
        {c.role !== 'open' && !guest ? (
          <div className="going-on-talk">
            <span className="going-on-label">When Jabberr replies</span>
            <TalkModeControl value={c.listenMode} onChange={onListenMode} busy={listenBusy} showHint />
          </div>
        ) : null}
      </div>
    </aside>
  );
}

function Fact({ title, text }: { title: string; text: string }) {
  return (
    <div className="going-on-fact">
      <span className="going-on-fact-title">{title}</span>
      <span className="going-on-fact-text">{text}</span>
    </div>
  );
}

function more(first: string, rest: number): string {
  return rest > 0 ? `${first} and ${rest} more` : first;
}

/** The first readable line of a state document, without its title. */
function firstLine(content: string): string | null {
  const { lede, sections } = docSections(content);
  const text = (lede || sections[0]?.body || '').split('\n').map((l) => l.replace(/^[-*#>\s]+/, '').trim()).find(Boolean);
  if (!text) return null;
  return text.length > 90 ? `${text.slice(0, 88)}...` : text;
}
